import React, { useMemo } from 'react'
import ReactHighcharts from 'react-highcharts'
import numeral from 'numeral'
import moment from 'moment'
import { hcColors, hcFontFamily } from 'modules/chart'

interface AIChartProps {
  reportData: {
    data?: Record<string, unknown>
    stats?: Record<string, unknown>
  }
  reportType?: string
}

const chartColors = hcColors

const baseChartConfig = {
  credits: { enabled: false },
  title: { text: null },
  legend: {
    enabled: true,
    itemStyle: { fontFamily: hcFontFamily, fontSize: '11px' },
  },
  chart: {
    style: { fontFamily: hcFontFamily },
    backgroundColor: 'transparent',
  },
}

// Format values based on metric type
const formatValue = (value: number, metricKey: string): string => {
  if (value === null || value === undefined) return '-'
  const key = metricKey.toLowerCase()
  const currencyMetrics = ['sales', 'total_sales', 'net_sales', 'profit', 'revenue', 'price', 'cost', 'tax', 'shipping', 'discount', 'inventory_value', 'inventory_cost', 'total_inventory_value', 'total_inventory_cost']
  const percentMetrics = ['margin', 'rate', 'percentage', 'sell_through']

  if (currencyMetrics.some(m => key.includes(m))) {
    return numeral(value).format('$0,0')
  }
  if (percentMetrics.some(m => key.includes(m))) {
    return numeral(value).format('0.0%')
  }
  return numeral(value).format('0,0')
}

// Detect if a value is currency-based
const isCurrencyMetric = (key: string): boolean => {
  const k = key.toLowerCase()
  return ['sales', 'total_sales', 'net_sales', 'profit', 'revenue', 'price', 'cost', 'tax', 'shipping', 'discount', 'inventory_value', 'inventory_cost', 'total_inventory_value', 'total_inventory_cost'].some(m => k.includes(m))
}

// ============ Individual Chart Components ============

// Bar chart for top items (products, customers, etc.)
const TopItemsBarChart: React.FC<{
  data: Array<Record<string, unknown>>
  labelKey: string
  valueKey: string
  title: string
  limit?: number
}> = React.memo(({ data, labelKey, valueKey, title, limit = 8 }) => {
  const config = useMemo(() => {
    if (!data || data.length === 0) return null

    const limitedData = data.slice(0, limit)
    const categories = limitedData.map(d => {
      const label = d[labelKey] || d.title || d.name || d.value || 'Unknown'
      return String(label).slice(0, 25) + (String(label).length > 25 ? '...' : '')
    })
    const values = limitedData.map(d => Number(d[valueKey]) || 0)

    if (values.every(v => v === 0)) return null

    const isCurrency = isCurrencyMetric(valueKey)

    return {
      ...baseChartConfig,
      chart: { ...baseChartConfig.chart, type: 'bar', height: Math.min(200 + limitedData.length * 25, 350) },
      title: { text: title, style: { fontSize: '13px', fontWeight: '600' } },
      xAxis: {
        categories,
        labels: { style: { fontSize: '10px' } },
      },
      yAxis: {
        title: { text: null },
        labels: {
          formatter: function(this: { value: number }) {
            return isCurrency ? numeral(this.value).format('$0a') : numeral(this.value).format('0a')
          },
        },
      },
      tooltip: {
        formatter: function(this: { point: { category: string; y: number } }) {
          return `<b>${this.point.category}:</b> ${formatValue(this.point.y, valueKey)}`
        },
      },
      legend: { enabled: false },
      plotOptions: {
        bar: {
          colorByPoint: true,
          colors: chartColors,
          dataLabels: {
            enabled: true,
            formatter: function(this: { y: number }) {
              return isCurrency ? numeral(this.y).format('$0a') : numeral(this.y).format('0a')
            },
            style: { fontSize: '10px', fontWeight: 'normal' },
          },
        },
      },
      series: [{ name: title, data: values }],
    }
  }, [data, labelKey, valueKey, title, limit])

  if (!config) return null
  return <ReactHighcharts config={config} isPureConfig={true} />
})

// Line/Area chart for time series
const TimeSeriesChart: React.FC<{
  data: Array<Record<string, unknown>>
  dateKey: string
  metrics: Array<{ key: string; name: string }>
  title: string
}> = React.memo(({ data, dateKey, metrics, title }) => {
  const config = useMemo(() => {
    if (!data || data.length < 2) return null

    const sortedData = [...data].sort((a, b) =>
      moment(a[dateKey] as string).valueOf() - moment(b[dateKey] as string).valueOf()
    )

    const categories = sortedData.map(d => moment(d[dateKey] as string).format('MMM DD'))
    const isCurrency = metrics.some(m => isCurrencyMetric(m.key))

    const series = metrics.map((metric, i) => ({
      name: metric.name,
      data: sortedData.map(d => Number(d[metric.key]) || 0),
      color: chartColors[i % chartColors.length],
    })).filter(s => s.data.some(v => v !== 0))

    if (series.length === 0) return null

    return {
      ...baseChartConfig,
      chart: { ...baseChartConfig.chart, type: 'areaspline', height: 220 },
      title: { text: title, style: { fontSize: '13px', fontWeight: '600' } },
      xAxis: {
        categories,
        labels: { style: { fontSize: '10px' } },
        tickInterval: Math.max(1, Math.ceil(categories.length / 8)),
      },
      yAxis: {
        title: { text: null },
        labels: {
          formatter: function(this: { value: number }) {
            return isCurrency ? numeral(this.value).format('$0a') : numeral(this.value).format('0a')
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: function(this: { points: Array<{ series: { name: string }; y: number }> }) {
          return this.points.map(p =>
            `<b>${p.series.name}:</b> ${formatValue(p.y, p.series.name)}`
          ).join('<br/>')
        },
      },
      plotOptions: {
        areaspline: {
          fillOpacity: 0.15,
          marker: { enabled: data.length < 15, radius: 3 },
          lineWidth: 2,
        },
      },
      series,
    }
  }, [data, dateKey, metrics, title])

  if (!config) return null
  return <ReactHighcharts config={config} isPureConfig={true} />
})

// Pie chart for distribution
const DistributionPieChart: React.FC<{
  data: Array<{ name: string; value: number }>
  title: string
}> = React.memo(({ data, title }) => {
  const config = useMemo(() => {
    if (!data || data.length === 0) return null

    const filteredData = data.filter(d => d.value > 0).slice(0, 8)
    if (filteredData.length === 0) return null

    return {
      ...baseChartConfig,
      chart: { ...baseChartConfig.chart, type: 'pie', height: 250 },
      title: { text: title, style: { fontSize: '13px', fontWeight: '600' } },
      tooltip: {
        formatter: function(this: { point: { name: string; y: number; percentage: number } }) {
          return `<b>${this.point.name}:</b> ${numeral(this.point.y).format('$0,0')} (${this.point.percentage.toFixed(1)}%)`
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: true,
          cursor: 'pointer',
          colors: chartColors,
          dataLabels: {
            enabled: true,
            format: '<b>{point.name}</b>: {point.percentage:.1f}%',
            style: { fontSize: '10px' },
          },
        },
      },
      series: [{
        name: title,
        data: filteredData.map(d => ({ name: d.name, y: d.value })),
      }],
    }
  }, [data, title])

  if (!config) return null
  return <ReactHighcharts config={config} isPureConfig={true} />
})

// Column chart for comparisons
const ComparisonColumnChart: React.FC<{
  categories: string[]
  series: Array<{ name: string; data: number[] }>
  title: string
  isCurrency?: boolean
}> = React.memo(({ categories, series, title, isCurrency = true }) => {
  const config = useMemo(() => {
    if (!categories || categories.length === 0 || !series || series.length === 0) return null

    return {
      ...baseChartConfig,
      chart: { ...baseChartConfig.chart, type: 'column', height: 250 },
      title: { text: title, style: { fontSize: '13px', fontWeight: '600' } },
      xAxis: {
        categories,
        labels: { style: { fontSize: '10px' } },
      },
      yAxis: {
        title: { text: null },
        labels: {
          formatter: function(this: { value: number }) {
            return isCurrency ? numeral(this.value).format('$0a') : numeral(this.value).format('0a')
          },
        },
      },
      tooltip: {
        shared: true,
        formatter: function(this: { points: Array<{ series: { name: string }; y: number }> }) {
          return this.points.map(p =>
            `<b>${p.series.name}:</b> ${isCurrency ? numeral(p.y).format('$0,0') : numeral(p.y).format('0,0')}`
          ).join('<br/>')
        },
      },
      plotOptions: {
        column: {
          grouping: true,
        },
      },
      series: series.map((s, i) => ({ ...s, color: chartColors[i % chartColors.length] })),
    }
  }, [categories, series, title, isCurrency])

  if (!config) return null
  return <ReactHighcharts config={config} isPureConfig={true} />
})

// ============ Main Component ============

/** The charts this report can show (empty when none apply), so callers can hide an empty tab. */
export const buildChartElements = (reportData: any, reportType?: string | null): React.ReactNode[] => {
    if (!reportData) return []

    const data = reportData.data || {}
    const stats = reportData.stats || {}
    const chartsToRender: React.ReactNode[] = []

    // Helper to get array data from various keys
    const getArrayData = (keys: string[]): Array<Record<string, unknown>> | null => {
      for (const key of keys) {
        const val = data[key]
        if (Array.isArray(val) && val.length > 0) return val
      }
      if (Array.isArray(data) && data.length > 0) return data
      return null
    }

    // 1. Try to create a time series chart if we have date-based data
    const trendsData = getArrayData(['trends', 'data', 'orders', 'lineItems'])
    if (trendsData && trendsData.length >= 2) {
      const dateKey = trendsData[0].date ? 'date' : trendsData[0].period_start ? 'period_start' : null
      if (dateKey) {
        const availableMetrics: Array<{ key: string; name: string }> = []
        if (trendsData[0].sales !== undefined) availableMetrics.push({ key: 'sales', name: 'Sales' })
        if (trendsData[0].total_orders !== undefined) availableMetrics.push({ key: 'total_orders', name: 'Orders' })
        if (trendsData[0].items_sold !== undefined) availableMetrics.push({ key: 'items_sold', name: 'Items Sold' })
        if (trendsData[0].customers !== undefined) availableMetrics.push({ key: 'customers', name: 'Customers' })

        if (availableMetrics.length > 0) {
          chartsToRender.push(
            <TimeSeriesChart
              key="trends"
              data={trendsData}
              dateKey={dateKey}
              metrics={availableMetrics.slice(0, 2)}
              title="Sales Over Time"
            />
          )
        }
      }
    }

    // Check if this is an inventory report first
    const isInventoryReport = reportType?.startsWith('inventory') || 
                              reportType?.includes('inventory') ||
                              reportType === 'inventoryProducts' || 
                              reportType === 'inventoryVariants' || 
                              reportType === 'inventoryBrands' ||
                              !!data.inventoryProducts ||
                              !!data.inventoryVariants ||
                              !!data.inventoryBrands ||
                              // Check if docs has inventory-like data
                              (data.docs && Array.isArray(data.docs) && data.docs.length > 0 && 
                               ((data.docs[0] as Record<string, unknown>)?.quantity !== undefined || 
                                (data.docs[0] as Record<string, unknown>)?.inventory_value !== undefined))

    // 2. Try to create top items bar chart for products/variants/customers/etc (skip for inventory)
    if (!isInventoryReport) {
      const productData = getArrayData(['products', 'variants', 'skus', 'brands'])
      if (productData && productData.length > 0) {
        const labelKey = productData[0].title ? 'title' : productData[0].name ? 'name' : productData[0].value ? 'value' : 'title'
        const valueKey = productData[0].sales !== undefined ? 'sales' :
                         productData[0].total_sales !== undefined ? 'total_sales' :
                         productData[0].items_sold !== undefined ? 'items_sold' : 'sales'

        chartsToRender.push(
          <TopItemsBarChart
            key="products"
            data={productData}
            labelKey={labelKey}
            valueKey={valueKey}
            title="Top Products by Sales"
            limit={8}
          />
        )
      }

      const customerData = getArrayData(['customers'])
      if (customerData && customerData.length > 0 && !productData) {
        chartsToRender.push(
          <TopItemsBarChart
            key="customers"
            data={customerData}
            labelKey="email"
            valueKey="total_sales"
            title="Top Customers by Sales"
            limit={8}
          />
        )
      }
    }

    // 2b. Handle inventory reports
    if (isInventoryReport) {
      // Inventory data is in .docs (similar to traffic reports)
      let inventoryData: Array<Record<string, unknown>> | null = null
      
      // Primary location: data.docs (same as traffic)
      if (data.docs && Array.isArray(data.docs)) {
        inventoryData = data.docs as Array<Record<string, unknown>>
      }
      
      // Fallback: try reportType key or inventory keys
      if (!inventoryData || inventoryData.length === 0) {
        if (reportType && data[reportType]) {
          inventoryData = data[reportType] as Array<Record<string, unknown>>
        }
      }
      if (!inventoryData || inventoryData.length === 0) {
        inventoryData = getArrayData(['inventoryProducts', 'inventoryVariants', 'inventoryBrands'])
      }
      
      // Also check if the data itself is the inventory array (direct array structure)
      if ((!inventoryData || inventoryData.length === 0) && Array.isArray(data) && data.length > 0 && data[0]?.quantity !== undefined) {
        inventoryData = data as Array<Record<string, unknown>>
      }
      
      if (inventoryData && inventoryData.length > 0) {
        // Determine report type from reportType prop or data keys
        const isBrands = reportType === 'inventoryBrands' || !!data.inventoryBrands
        const isVariants = reportType === 'inventoryVariants' || !!data.inventoryVariants

        const labelKey = isBrands ? 'brand_name' :
                         inventoryData[0]?.product_name ? 'product_name' :
                         inventoryData[0]?.brand_name ? 'brand_name' : 'product_name'

        // Low stock items bar chart
        const lowStockItems = inventoryData.filter((item: Record<string, unknown>) => item.low_stock || Number(item.quantity || item.total_quantity || 0) < 10)
        if (lowStockItems.length > 0) {
          chartsToRender.push(
            <TopItemsBarChart
              key="lowStock"
              data={lowStockItems}
              labelKey={labelKey}
              valueKey={isBrands ? 'total_quantity' : 'quantity'}
              title="Low Stock Items"
              limit={8}
            />
          )
        }

        // Top items by inventory value
        if (inventoryData[0]?.inventory_value !== undefined || inventoryData[0]?.total_inventory_value !== undefined) {
          chartsToRender.push(
            <TopItemsBarChart
              key="inventoryValue"
              data={inventoryData}
              labelKey={labelKey}
              valueKey={isBrands ? 'total_inventory_value' : 'inventory_value'}
              title={isBrands ? 'Inventory Value by Brand' : 'Top Products by Inventory Value'}
              limit={8}
            />
          )
        }

        // Top items by quantity - show if we haven't added many charts yet
        if (chartsToRender.length < 2) {
          chartsToRender.push(
            <TopItemsBarChart
              key="inventoryQuantity"
              data={inventoryData}
              labelKey={labelKey}
              valueKey={isBrands ? 'total_quantity' : 'quantity'}
              title={isBrands ? 'Stock Quantity by Brand' : 'Stock Quantity by Product'}
              limit={8}
            />
          )
        }

        // Return early for inventory reports since other chart types don't apply
        if (chartsToRender.length > 0) {
          return chartsToRender.slice(0, 4)
        }
      }
    }

    // 3. Try to create distribution pie chart from facets
    const facets = data.facets as Record<string, unknown> || {}
    const facetKeys = Object.keys(facets)
    if (facetKeys.length > 0 && chartsToRender.length < 3) {
      const facetKey = facetKeys[0]
      const facet = facets[facetKey] as {
        categories?: string[]
        series?: Record<string, number[]>
        metric?: string
        title?: string
      }

      if (facet?.categories && facet?.series) {
        const metricKey = facet.metric || 'total_sales'
        const seriesData = facet.series[metricKey] || []

        if (seriesData.length > 0) {
          const pieData = facet.categories.slice(0, 8).map((cat, i) => ({
            name: cat,
            value: seriesData[i] || 0,
          }))

          chartsToRender.push(
            <DistributionPieChart
              key={`facet-${facetKey}`}
              data={pieData}
              title={facet.title || 'Sales Distribution'}
            />
          )
        }
      }
    }

    // 4. Year comparison chart
    const yearComparisons = data.yearComparisons as Record<string, unknown> || {}
    const years = Object.keys(yearComparisons).sort()
    if (years.length >= 2 && chartsToRender.length < 4) {
      const monthlyData: Record<string, Record<string, number>> = {}

      years.forEach(year => {
        const yearData = yearComparisons[year] as { trends?: Array<{ period_start: string; sales: number }> }
        monthlyData[year] = {}
        if (yearData?.trends) {
          yearData.trends.forEach(t => {
            const month = moment(t.period_start).format('MMM')
            monthlyData[year][month] = (monthlyData[year][month] || 0) + (t.sales || 0)
          })
        }
      })

      const allMonths = new Set<string>()
      Object.values(monthlyData).forEach(yd => {
        Object.keys(yd).forEach(m => allMonths.add(m))
      })
      const months = Array.from(allMonths)

      if (months.length > 0) {
        chartsToRender.push(
          <ComparisonColumnChart
            key="yearComparison"
            categories={months}
            series={years.map(year => ({
              name: year,
              data: months.map(m => monthlyData[year][m] || 0),
            }))}
            title="Year over Year Comparison"
          />
        )
      }
    }

    // 5. If we have stats but no charts yet, create a simple metrics comparison
    if (chartsToRender.length === 0 && Object.keys(stats).length > 0) {
      const metricPairs = [
        { key: 'total_sales', name: 'Sales' },
        { key: 'total_profit', name: 'Profit' },
        { key: 'cost_of_goods_sold', name: 'COGS' },
      ].filter(m => stats[m.key] !== undefined && stats[m.key] !== 0)

      if (metricPairs.length >= 2) {
        chartsToRender.push(
          <ComparisonColumnChart
            key="statsComparison"
            categories={['Current Period']}
            series={metricPairs.map(m => ({
              name: m.name,
              data: [Number(stats[m.key]) || 0],
            }))}
            title="Key Metrics"
          />
        )
      }
    }

    // Limit to 4 charts max
    return chartsToRender.slice(0, 4)
}

const AIChart: React.FC<AIChartProps> = React.memo(({ reportData, reportType }) => {
  const charts = useMemo(() => buildChartElements(reportData, reportType), [reportData, reportType])

  if (charts.length === 0) return null

  return (
    <div className={`tw-grid tw-gap-4 tw-mb-4 ${charts.length === 1 ? 'tw-grid-cols-1' : 'tw-grid-cols-1 md:tw-grid-cols-2'}`}>
      {charts.map((chart, index) => (
        <div key={index} className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4">
          {chart}
        </div>
      ))}
    </div>
  )
})

export default AIChart
